import { X } from "lucide-react";
import RegulariseChecksRequestModal from "./RegulariseChecksRequestModal";

const resolveFormActionLabel = (label) =>
  label === "Tax Declaration" ? "Investment Declaration" : label;

const QuickRequestFormModal = ({
  darkMode = false,
  isOpen,
  actionLabel,
  context,
  quickActionDraft,
  setQuickActionDraft,
  maxDateToday,
  onClose,
  onSubmitQuickAction,
  onSubmitRegularise,
}) => {
  if (!isOpen || !actionLabel) return null;

  const formActionLabel = resolveFormActionLabel(actionLabel);
  const inputCls =
    "mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-[#3C3C4C] dark:bg-[#1D1D1D]";

  if (formActionLabel === "Regularise checks") {
    return (
      <RegulariseChecksRequestModal
        isOpen
        darkMode={darkMode}
        zIndexClass="z-[10020]"
        employeeId={context?.employeeId}
        employeeName={context?.userName || "Current user"}
        initialDateIso={quickActionDraft.date || maxDateToday}
        onClose={onClose}
        onSubmit={onSubmitRegularise}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-xl rounded-lg border border-gray-200 bg-white text-black shadow-xl dark:border-[#3C3C4C] dark:bg-[#121212] dark:text-gray-100">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-[#3C3C4C]">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {actionLabel}
            </h3>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
              Employee: {context?.userName || "Current user"}
              {context?.employeeId ? ` (ID: ${context.employeeId})` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#1a1a1a]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmitQuickAction} className="space-y-3 p-4">
          {formActionLabel === "Leave" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Leave Type
                  <select
                    value={quickActionDraft.leaveType}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, leaveType: e.target.value }))
                    }
                    className={inputCls}
                  >
                    <option value="CL">CL</option>
                    <option value="PL">PL</option>
                    <option value="SL">SL</option>
                    <option value="LOP">LOP</option>
                  </select>
                </label>
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Leave Duration
                  <select
                    value={quickActionDraft.leaveDuration}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, leaveDuration: e.target.value }))
                    }
                    className={inputCls}
                  >
                    <option value="full_day">Full Day</option>
                    <option value="first_half">First Half</option>
                    <option value="second_half">Second Half</option>
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  From Date
                  <input
                    type="date"
                    required
                    value={quickActionDraft.fromDate}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, fromDate: e.target.value }))
                    }
                    className={inputCls}
                  />
                </label>
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  To Date
                  <input
                    type="date"
                    required
                    value={quickActionDraft.toDate}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, toDate: e.target.value }))
                    }
                    className={inputCls}
                  />
                </label>
              </div>
            </>
          )}

          {formActionLabel === "Change Shift" && (
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs text-gray-700 dark:text-gray-300">
                Effective Date
                <input
                  type="date"
                  required
                  value={quickActionDraft.date}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, date: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="text-xs text-gray-700 dark:text-gray-300">
                Current Shift
                <input
                  type="text"
                  value={quickActionDraft.currentShift}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, currentShift: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="text-xs text-gray-700 dark:text-gray-300">
                New Shift
                <input
                  type="text"
                  required
                  value={quickActionDraft.shift}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, shift: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
            </div>
          )}

          {formActionLabel === "Short Leave" && (
            <div className="grid grid-cols-3 gap-3">
              <label className="text-xs text-gray-700 dark:text-gray-300">
                Date
                <input
                  type="date"
                  required
                  value={quickActionDraft.date}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, date: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="text-xs text-gray-700 dark:text-gray-300">
                Start Time
                <input
                  type="time"
                  required
                  value={quickActionDraft.startTime}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, startTime: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="text-xs text-gray-700 dark:text-gray-300">
                End Time
                <input
                  type="time"
                  required
                  value={quickActionDraft.endTime}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, endTime: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
            </div>
          )}

          {formActionLabel === "Resignation" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Resignation Date
                  <input
                    type="date"
                    required
                    value={quickActionDraft.resignationDate}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, resignationDate: e.target.value }))
                    }
                    className={inputCls}
                  />
                </label>
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Last Working Date
                  <input
                    type="date"
                    required
                    value={quickActionDraft.lastWorkingDate}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, lastWorkingDate: e.target.value }))
                    }
                    className={inputCls}
                  />
                </label>
              </div>
              <label className="block text-xs text-gray-700 dark:text-gray-300">
                Resignation Reason
                <select
                  required
                  value={quickActionDraft.resignationReason}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, resignationReason: e.target.value }))
                  }
                  className={inputCls}
                >
                  <option value="">Select reason</option>
                  <option value="career_growth">Career Growth</option>
                  <option value="personal_reason">Personal Reason</option>
                  <option value="higher_studies">Higher Studies</option>
                  <option value="relocation">Relocation</option>
                  <option value="health">Health</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </>
          )}

          {formActionLabel === "Ticket" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Ticket Type
                  <select
                    value={quickActionDraft.ticketType}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, ticketType: e.target.value }))
                    }
                    className={inputCls}
                  >
                    <option value="">Select type</option>
                    <option value="it_support">IT Support</option>
                    <option value="payroll">Payroll</option>
                    <option value="attendance">Attendance</option>
                    <option value="hr_query">HR Query</option>
                    <option value="asset_request">Asset Request</option>
                  </select>
                </label>
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Priority
                  <select
                    value={quickActionDraft.ticketPriority}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, ticketPriority: e.target.value }))
                    }
                    className={inputCls}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </label>
              </div>
              <label className="block text-xs text-gray-700 dark:text-gray-300">
                Subject
                <input
                  type="text"
                  value={quickActionDraft.ticketSubject}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, ticketSubject: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
            </>
          )}

          {formActionLabel === "Investment Declaration" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Financial Year
                  <input
                    type="text"
                    value={quickActionDraft.financialYear}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, financialYear: e.target.value }))
                    }
                    className={inputCls}
                  />
                </label>
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Tax Regime
                  <select
                    value={quickActionDraft.taxRegime}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, taxRegime: e.target.value }))
                    }
                    className={inputCls}
                  >
                    <option value="new">New Regime</option>
                    <option value="old">Old Regime</option>
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Section
                  <input
                    type="text"
                    value={quickActionDraft.investmentSection}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, investmentSection: e.target.value }))
                    }
                    className={inputCls}
                  />
                </label>
                <label className="text-xs text-gray-700 dark:text-gray-300">
                  Declared Amount
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={quickActionDraft.amount}
                    onChange={(e) =>
                      setQuickActionDraft((p) => ({ ...p, amount: e.target.value }))
                    }
                    className={inputCls}
                  />
                </label>
              </div>
            </>
          )}

          {formActionLabel === "MRF" && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-gray-700 dark:text-gray-300">
                Department
                <input
                  type="text"
                  value={quickActionDraft.requestDepartment}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, requestDepartment: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="text-xs text-gray-700 dark:text-gray-300">
                Designation
                <input
                  type="text"
                  value={quickActionDraft.requestDesignation}
                  onChange={(e) =>
                    setQuickActionDraft((p) => ({ ...p, requestDesignation: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
            </div>
          )}

          <label className="block text-xs text-gray-700 dark:text-gray-300">
            Reason
            <textarea
              required
              rows={3}
              value={quickActionDraft.reason}
              onChange={(e) =>
                setQuickActionDraft((p) => ({ ...p, reason: e.target.value }))
              }
              className={inputCls}
            />
          </label>
          <label className="block text-xs text-gray-700 dark:text-gray-300">
            Notes (optional)
            <textarea
              rows={2}
              value={quickActionDraft.notes}
              onChange={(e) =>
                setQuickActionDraft((p) => ({ ...p, notes: e.target.value }))
              }
              className={inputCls}
            />
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-200 dark:border-[#3C3C4C] dark:bg-[#1D1D1D] dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md border border-redhalo-red bg-redhalo-red px-3 py-1.5 text-xs font-medium text-white hover:opacity-95"
            >
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickRequestFormModal;
